'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Bookmark, Plus, Search } from 'lucide-react';
import { AuthButton } from '@/components/auth-button';
import { useAuth } from '@/components/providers/auth-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function HomeHeader() {
  const { user, loading } = useAuth();
  const pathname = usePathname();
  const navItems = [
    { href: '/discover', label: 'Artworks' },
    { href: '/artists', label: 'Artists' },
    { href: '/sets', label: 'Sets' },
    { href: '/blog', label: 'Blog' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex h-[86px] max-w-7xl items-center gap-6 px-5 sm:px-8">
        <Link href="/" className="flex min-w-fit items-center gap-3" aria-label="Art Vault home">
          <div className="font-sans text-4xl font-semibold tracking-normal leading-none">AV</div>
          <div className="leading-tight">
            <div className="font-sans text-lg font-semibold tracking-[0.08em]">ART VAULT</div>
            <div className="text-xs text-muted-foreground">Catalog. Discover. Preserve.</div>
          </div>
        </Link>

        <form action="/discover" className="hidden flex-1 justify-center md:flex">
          <div className="relative w-full max-w-[420px]">
            <Input
              name="search"
              placeholder="Search artworks, artists, sets..."
              className="h-12 rounded-md border-border/80 bg-background pr-11 text-sm"
            />
            <Search className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </form>

        <nav className="ml-auto hidden items-center gap-7 text-xs font-semibold uppercase tracking-normal lg:flex">
          {navItems.map((item) => {
            const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
            return (
              <Link
                key={item.href}
                href={item.href}
                className={isActive ? 'border-b border-primary pb-2' : 'pb-2 hover:text-muted-foreground'}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-3 lg:ml-0">
          {!loading && user && (
            <Button asChild size="icon" variant="outline" className="h-9 w-9 rounded-full">
              <Link href="/artworks/new" aria-label="Add artwork">
                <Plus className="h-4 w-4" />
              </Link>
            </Button>
          )}
          <Button size="icon" variant="ghost" className="hidden h-9 w-9 sm:inline-flex" aria-label="Bookmarks">
            <Bookmark className="h-5 w-5" />
          </Button>
          <AuthButton />
        </div>
      </div>
    </header>
  );
}
